import { CustomerInvoiceAttachmentCard } from './CustomerInvoiceAttachmentCard';
import type { CustomerInvoiceAttachmentView } from '../types';

interface CustomerInvoiceAttachmentGridProps {
  attachments: CustomerInvoiceAttachmentView[];
  onAttachmentClick?: (attachment: CustomerInvoiceAttachmentView) => void;
  onDelete?: (attachment: CustomerInvoiceAttachmentView) => void;
  emptyMessage?: string;
  showDeleteButton?: boolean;
  isLoading?: boolean;
}

export function CustomerInvoiceAttachmentGrid({
  attachments,
  onAttachmentClick,
  onDelete,
  emptyMessage = 'No customer documents yet',
  showDeleteButton = false,
  isLoading = false,
}: CustomerInvoiceAttachmentGridProps) {
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="aspect-square bg-secondary-200 rounded-lg" />
            <div className="mt-2 space-y-2">
              <div className="h-4 bg-secondary-200 rounded w-3/4" />
              <div className="h-3 bg-secondary-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (attachments.length === 0) {
    return (
      <div className="text-center py-12 bg-secondary-50 rounded-lg">
        <svg
          className="w-12 h-12 mx-auto text-secondary-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="mt-2 text-secondary-600">{emptyMessage}</p>
        <p className="mt-1 text-sm text-secondary-500">
          Upload quotes, contracts, and invoices for your customer
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      {attachments.map((attachment) => (
        <CustomerInvoiceAttachmentCard
          key={attachment.id}
          attachment={attachment}
          onClick={() => onAttachmentClick?.(attachment)}
          onDelete={() => onDelete?.(attachment)}
          showDeleteButton={showDeleteButton}
        />
      ))}
    </div>
  );
}
